import { tr } from '@/lib/i18n';
import type { ReactNode } from 'react';
import { Menu, MoreHorizontal, MoreVertical, Plus, Download, ChevronDown, Check } from 'lucide-react';
import { isAndroidDevice, isIOSChromeBrowser, isIOSDevice } from '@/utils/platform';

// ============================================================================
// Como instalar o app, uma fonte só para o diálogo da home e a tela do cadastro
// (antes eram duas listas que se contradiziam: uma mandava baixar o Chrome, a
// outra ensinava o Safari com o botão errado).
//
// 25/09/2026, percorrido no Safari do simulador de iPhone (iOS 27), do começo ao
// ícone na tela de início: botão ≡ ao lado do endereço → "Share" → "View More"
// (a seta para baixo; "Add to Home Screen" não está na primeira fileira) →
// "Add to Home Screen" → "Add" no alto (o "Open as Web App" já vem ligado).
// O Safari VOLTOU por decisão dele no mesmo dia ("se está funcionando pode
// voltar pra ela"), revogando a regra "Chrome, nunca Safari" de 17/08.
//
// O que ninguém avisava: o app instalado NÃO herda o login do navegador. Ele
// abre na tela de entrada. Sem aviso, isso parece "não funcionou".
// ============================================================================

export type InstallPlatform = 'ios-safari' | 'ios-chrome' | 'android' | 'desktop';

export interface InstallStep {
  icon: ReactNode;
  text: ReactNode;
}

export function installPlatform(): InstallPlatform {
  if (isIOSChromeBrowser()) return 'ios-chrome';
  if (isIOSDevice()) return 'ios-safari';
  if (isAndroidDevice()) return 'android';
  return 'desktop';
}

const b = (s: string) => <strong className="font-bold">{s}</strong>;

export const INSTALL_STEPS: Record<InstallPlatform, InstallStep[]> = {
  'ios-safari': [
    {
      icon: <Menu className="h-6 w-6" />,
      text: <>{tr("Tap the ")}{b('≡')}{tr(" or ")}{b('•••')}{tr(" button next to the web address at the bottom of the screen.")}</>,
    },
    {
      icon: <img src="/ios-share-icon.webp" alt="" className="h-6 w-6 object-contain" />,
      text: <>{tr("Tap ")}{b(tr("Share"))}{tr(". (On older iPhones, the Share button is already at the bottom.)")}</>,
    },
    {
      icon: <ChevronDown className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("View More"))}{tr(", then ")}{b(tr("Add to Home Screen"))}.</>,
    },
    {
      icon: <Check className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("Add"))}{tr(" at the top right.")}</>,
    },
  ],
  'ios-chrome': [
    {
      icon: <img src="/ios-share-icon.webp" alt="" className="h-6 w-6 object-contain" />,
      text: <>{tr("Tap the ")}{b(tr("Share"))}{tr(" button next to the web address.")}</>,
    },
    {
      icon: <MoreHorizontal className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("View More"))}{tr(" if you don’t see the next option.")}</>,
    },
    {
      icon: <Plus className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("Add to Home Screen"))}{tr(", then ")}{b(tr("Add"))}.</>,
    },
  ],
  android: [
    {
      icon: <MoreVertical className="h-6 w-6" />,
      text: <>{tr("Tap the ")}{b('⋮')}{tr(" menu in the top-right corner.")}</>,
    },
    {
      icon: <Download className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("Install app"))}{tr(" or ")}{b(tr("Add to Home screen"))}.</>,
    },
    {
      icon: <Plus className="h-6 w-6" />,
      text: <>{tr("Tap ")}{b(tr("Install"))}{tr(" to confirm.")}</>,
    },
  ],
  desktop: [
    {
      icon: <Download className="h-6 w-6" />,
      text: <>{tr("Look for the install icon in the address bar.")}</>,
    },
    {
      icon: <Plus className="h-6 w-6" />,
      text: <>{tr("Click ")}{b(tr("Install"))}.</>,
    },
  ],
};

/** Vale para todos: o app novo abre na tela de entrada. */
export const SIGN_IN_AGAIN =
  tr("Then open the new Obedience Code icon and sign in once with the same email. After that, it stays signed in.");

export function InstallStepList({ steps }: { steps: InstallStep[] }) {
  return (
    <ol className="space-y-4">
      {steps.map((step, i) => (
        <li key={i} className="flex items-start gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-muted text-foreground">
            {step.icon}
          </span>
          <span className="pt-1.5 text-[17px] leading-snug text-foreground">
            <span className="font-bold text-accent">{i + 1}.</span> {step.text}
          </span>
        </li>
      ))}
    </ol>
  );
}
